import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Mail, ArrowRight, Atom, KeyRound, ArrowLeft, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import StemBackgroundDecorations from '../components/StemBackgroundDecorations';

// Safe localStorage wrapper
const safeStorage = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch { console.warn('Storage unavailable'); } },
  removeItem: (key) => { try { localStorage.removeItem(key); } catch { console.warn('Storage unavailable'); } }
};

const StudentLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/students/login/send-otp`, { email: email.toLowerCase().trim() });
      toast.success('Verification code sent to your email');
      setOtpSent(true);
      setStep('otp');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send verification code';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/students/login/verify-otp`, {
        email: email.toLowerCase().trim(),
        otp: otp.trim()
      });

      // Store student token and info
      safeStorage.setItem('studentToken', res.data.token);
      safeStorage.setItem('studentId', res.data.student.id);
      safeStorage.setItem('studentName', res.data.student.full_name);
      safeStorage.setItem('studentEmail', res.data.student.email);

      toast.success('Login successful!');
      navigate('/student/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid verification code';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/students/login/send-otp`, { email: email.toLowerCase().trim() });
      toast.success('New verification code sent');
    } catch (err) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-deep-blue overflow-hidden">
      <StemBackgroundDecorations />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center">
              <Atom className="w-7 h-7 text-electric-cyan" />
            </div>
            <span className="font-outfit font-bold text-2xl text-white">
              Physics<span className="text-electric-cyan">Co</span>
            </span>
          </Link>

          {/* Login Card */}
          <div className="glass-card p-8" data-testid="student-login-card">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-electric-cyan/20 flex items-center justify-center">
                <LogIn className="w-8 h-8 text-electric-cyan" />
              </div>
              <h1 className="font-outfit text-2xl font-bold text-white mb-2">Student Login</h1>
              <p className="text-slate-400 text-sm">
                {step === 'email' 
                  ? 'Enter your email to receive a verification code'
                  : `Enter the code sent to ${email}`
                }
              </p>
            </div>

            {step === 'email' ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="input-dark pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                  data-testid="login-send-otp-btn"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Verification Code
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Verification Code</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="input-dark pl-10 text-center text-xl tracking-widest font-mono"
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="login-otp-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="btn-primary w-full"
                  data-testid="login-verify-btn"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify & Login
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => { setStep('email'); setOtp(''); }}
                    className="text-sm text-slate-400 hover:text-white flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Change email
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-sm text-electric-cyan hover:text-electric-cyan/80"
                  >
                    Resend code
                  </button>
                </div>
              </form>
            )}

            {/* Register link */}
            <div className="mt-6 pt-6 border-t border-surface-navy text-center">
              <p className="text-slate-400 text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="text-electric-cyan hover:underline font-medium">
                  Register here
                </Link>
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <Link to="/" className="text-slate-400 hover:text-white text-sm flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentLogin;
